import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import AppShell from "@/components/layout/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Copy, Trash2, Eye, EyeOff, Archive, ChevronUp, ChevronDown, GripVertical, FileText, Inbox, Edit3, X, Check, Save, RotateCcw, Info, Layers, Printer, Download, Glasses, ToggleLeft, ToggleRight, Link2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Side-by-side pair detection
const PRINT_SIDE_BY_SIDE = [
  [/mother|mom|parent\s*1|guardian\s*1/i, /father|dad|parent\s*2|guardian\s*2/i],
  [/parent\s*\/\s*guardian\s*1/i, /parent\s*\/\s*guardian\s*2/i],
];

function groupPrintSections(fields) {
  const sections = [];
  let cur = { heading: null, fields: [] };
  for (const f of fields) {
    if (f.type === "section-heading") {
      if (cur.heading || cur.fields.length) sections.push({ ...cur });
      cur = { heading: f, fields: [] };
    } else { cur.fields.push(f); }
  }
  if (cur.heading || cur.fields.length) sections.push(cur);
  return sections;
}

function printField(f) {
  const req = f.required ? ' <span style="color:#ef4444">*</span>' : "";
  const help = f.helpText ? `<p style="font-size:9px;color:#94a3b8;margin:1px 0 3px">${f.helpText}</p>` : "";
  let input = "";
  if (["text","email","phone","number","date"].includes(f.type))
    input = `<div style="border:1px solid #cbd5e1;border-radius:6px;padding:5px 8px;min-height:22px;background:#fff;font-size:10px;color:#94a3b8">${f.placeholder||""}</div>`;
  else if (f.type === "textarea")
    input = `<div style="border:1px solid #cbd5e1;border-radius:6px;padding:5px 8px;min-height:40px;background:#fff"></div>`;
  else if (f.type === "signature")
    input = `<div style="border:2px dashed #cbd5e1;border-radius:6px;height:44px;background:#fff;display:flex;align-items:center;justify-content:center;font-size:10px;color:#94a3b8">Sign here</div>`;
  else if (["radio","checkbox-group","checkbox"].includes(f.type))
    input = `<div style="display:flex;flex-wrap:wrap;gap:4px 12px">${(f.options||[]).map(o => `<span style="display:inline-flex;align-items:center;gap:4px;font-size:10px"><span style="width:10px;height:10px;border:1.5px solid #94a3b8;border-radius:${f.type==="radio"?"50%":"2px"};display:inline-block;flex-shrink:0"></span>${o}</span>`).join("")}</div>`;
  else if (f.type === "dropdown")
    input = `<div style="border:1px solid #cbd5e1;border-radius:6px;padding:5px 8px;min-height:22px;background:#fff;font-size:10px;color:#94a3b8">${f.placeholder||"Select..."} v</div>`;
  else if (f.type === "file")
    input = `<div style="border:1px solid #cbd5e1;border-radius:6px;padding:5px 8px;background:#fff;font-size:10px;color:#94a3b8">Attach file...</div>`;
  return `<div style="margin-bottom:7px"><label style="font-size:10px;font-weight:600;color:#334155;display:block;margin-bottom:2px">${f.label}${req}</label>${help}${input}</div>`;
}

function printSectionHtml(s) {
  const heading = s.heading ? `<div style="background:#f0fdf4;border-bottom:1px solid #bbf7d0;padding:4px 10px;border-radius:8px 8px 0 0"><p style="font-size:10px;font-weight:700;color:#0f766e">${s.heading.label}</p></div>` : "";
  // Group consecutive halfWidth fields into 2-column rows
  const rows = [];
  let i = 0;
  while (i < s.fields.length) {
    const f = s.fields[i];
    if (f.type === "instruction") {
      rows.push(`<p style="font-size:9px;color:#475569;background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;padding:5px 8px;margin-bottom:6px;font-style:italic">${f.label}</p>`);
      i++;
    } else if (f.halfWidth && s.fields[i+1]?.halfWidth) {
      rows.push(`<table style="width:100%;border-collapse:separate;border-spacing:6px 0;margin-bottom:6px"><tr><td style="width:50%;vertical-align:top">${printField(f)}</td><td style="width:50%;vertical-align:top">${printField(s.fields[i+1])}</td></tr></table>`);
      i += 2;
    } else {
      rows.push(printField(f));
      i++;
    }
  }
  return `<div style="border:1px solid #e2e8f0;border-radius:8px;margin-bottom:8px;page-break-inside:avoid">${heading}<div style="padding:8px 10px">${rows.join("")}</div></div>`;
}

function buildPrintHtml(form, logoUrl, centerName) {
  const headerHtml = form.headerText ? `<div style="border:1px solid #e2e8f0;border-radius:6px;background:#f8fafc;padding:6px 10px;margin-bottom:10px;font-size:9px;color:#475569;white-space:pre-wrap">${form.headerText}</div>` : "";
  const footerHtml = form.footerText ? `<div style="border:1px solid #e2e8f0;border-radius:6px;background:#f8fafc;padding:6px 10px;margin-top:10px;font-size:9px;color:#64748b;white-space:pre-wrap">${form.footerText}</div>` : "";
  const sorted = (form.fields || []).slice().sort((a, b) => a.order - b.order);
  const sections = groupPrintSections(sorted);
  let bodyHtml = "";
  let i = 0;
  while (i < sections.length) {
    const s = sections[i];
    const next = sections[i + 1];
    const isPair = next && PRINT_SIDE_BY_SIDE.some(([p1, p2]) =>
      s.heading && next.heading && p1.test(s.heading.label) && p2.test(next.heading.label));
    if (isPair) {
      bodyHtml += `<table style="width:100%;border-collapse:separate;border-spacing:6px 0;margin-bottom:2px"><tr><td style="width:50%;vertical-align:top">${printSectionHtml(s)}</td><td style="width:50%;vertical-align:top">${printSectionHtml(next)}</td></tr></table>`;
      i += 2;
    } else if (s.heading) {
      bodyHtml += printSectionHtml(s); i++;
    } else {
      bodyHtml += s.fields.map(f => f.type === "instruction" ? `<p style="font-size:9px;color:#475569;background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;padding:5px 8px;margin-bottom:8px;font-style:italic">${f.label}</p>` : printField(f)).join("");
      i++;
    }
  }
  const html = `<!DOCTYPE html><html><head><title>${form.title} - ${centerName}</title>
<style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:10px;padding:0.35in}@media print{@page{size:letter;margin:0.35in}body{padding:0}}</style>
</head><body>
<div style="display:flex;align-items:center;border-bottom:2px solid #0f766e;padding-bottom:8px;margin-bottom:10px">
  <img src="${logoUrl}" style="height:38px;width:38px;object-fit:contain;flex-shrink:0" onerror="this.style.display='none'" />
  <div style="flex:1;text-align:center"><p style="font-size:15px;font-weight:bold;color:#0f766e">${centerName}</p><p style="font-size:12px;font-weight:600;color:#334155;margin-top:1px">${form.title}</p></div>
  <div style="width:38px"></div>
</div>
${headerHtml}${bodyHtml}${footerHtml}
<script>window.onload=function(){window.print();}</script>
</body></html>`;
  const win = window.open("", "_blank");
  if (win) { win.document.write(html); win.document.close(); }
}

const FIELD_TYPES = [
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "radio", label: "Multiple Choice" },
  { value: "checkbox", label: "Single Checkbox" },
  { value: "checkbox-group", label: "Checkboxes (multiple)" },
  { value: "dropdown", label: "Dropdown" },
  { value: "signature", label: "Signature" },
  { value: "file", label: "File Upload" },
  { value: "section-heading", label: "Section Heading" },
  { value: "instruction", label: "Instruction Text" },
];

const STATUS_COLORS = {
  draft: "bg-amber-100 text-amber-700",
  published: "bg-emerald-100 text-emerald-700",
  archived: "bg-slate-100 text-slate-500",
};

function FieldEditor({ field, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, onDragStart, onDragOver, onDrop, isDragOver }) {
  const [expanded, setExpanded] = useState(false);
  const [optionInput, setOptionInput] = useState("");
  const needsOptions = ["radio", "checkbox-group", "dropdown"].includes(field.type);
  const isStructural = ["section-heading", "instruction"].includes(field.type);

  return (
    <div
      className={`rounded-xl border bg-white transition-all ${isDragOver ? "border-teal-400 ring-2 ring-teal-100" : "border-slate-200"}`}
      data-testid={`field-editor-${field.id}`}
      draggable
      onDragStart={e => { e.dataTransfer.effectAllowed = "move"; onDragStart && onDragStart(field.id); }}
      onDragOver={e => { e.preventDefault(); onDragOver && onDragOver(field.id); }}
      onDrop={e => { e.preventDefault(); onDrop && onDrop(field.id); }}
      onDragEnd={() => onDrop && onDrop(null)}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <GripVertical className="h-4 w-4 text-slate-400 flex-shrink-0 cursor-grab active:cursor-grabbing" title="Drag to reorder" />
        <div className="flex-1 min-w-0">
          <input
            value={field.label}
            onChange={e => onChange({ ...field, label: e.target.value })}
            className="w-full text-sm font-medium text-slate-800 bg-transparent border-0 outline-none focus:ring-0 p-0"
            placeholder={isStructural ? "Section title..." : "Field label..."}
          />
        </div>
        <select
          value={field.type}
          onChange={e => onChange({ ...field, type: e.target.value })}
          className="text-xs border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 bg-white"
        >
          {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {!isStructural && (
          <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer select-none">
            <input type="checkbox" checked={field.required} onChange={e => onChange({ ...field, required: e.target.checked })} className="accent-emerald-600" />
            Req
          </label>
        )}
        <button onClick={() => setExpanded(v => !v)} className="text-slate-400 hover:text-slate-600">
          <Edit3 className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => !isFirst && onMoveUp()} disabled={isFirst} className="text-slate-300 hover:text-slate-500 disabled:opacity-20">
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => !isLast && onMoveDown()} disabled={isLast} className="text-slate-300 hover:text-slate-500 disabled:opacity-20">
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        <button onClick={onDelete} className="text-red-300 hover:text-red-500">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-3 py-3 space-y-2 bg-slate-50 rounded-b-xl">
          {!isStructural && (
            <input
              value={field.placeholder || ""}
              onChange={e => onChange({ ...field, placeholder: e.target.value })}
              className="w-full text-xs border border-slate-200 rounded px-2 py-1"
              placeholder="Placeholder text..."
            />
          )}
          <input
            value={field.helpText || ""}
            onChange={e => onChange({ ...field, helpText: e.target.value })}
            className="w-full text-xs border border-slate-200 rounded px-2 py-1"
            placeholder="Help text shown below the field..."
          />
          {needsOptions && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold text-slate-500 uppercase">Options</p>
              {(field.options || []).map((opt, i) => (
                <div key={i} className="flex items-center gap-1">
                  <span className="text-xs flex-1 text-slate-700">{opt}</span>
                  <button onClick={() => onChange({ ...field, options: field.options.filter((_, j) => j !== i) })} className="text-red-300 hover:text-red-500">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <div className="flex gap-1">
                <input value={optionInput} onChange={e => setOptionInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && optionInput.trim()) { onChange({ ...field, options: [...(field.options || []), optionInput.trim()] }); setOptionInput(""); }}}
                  className="flex-1 text-xs border border-slate-200 rounded px-2 py-1" placeholder="Add option..." />
                <button onClick={() => { if (optionInput.trim()) { onChange({ ...field, options: [...(field.options || []), optionInput.trim()] }); setOptionInput(""); }}}
                  className="text-xs bg-emerald-600 text-white px-2 py-1 rounded">Add</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FormBuilderEditor({ template, onSave, onClose }) {
  const [title, setTitle] = useState(template.title);
  const [description, setDescription] = useState(template.description || "");
  const [fields, setFields] = useState(template.fields || []);
  const [saving, setSaving] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [expirationMonths, setExpirationMonths] = useState(template.expirationMonths || 0);
  const [formDateFieldId, setFormDateFieldId] = useState(template.formDateFieldId || "");
  const [customStartDate, setCustomStartDate] = useState(template.customStartDate || "");
  const [headerText, setHeaderText] = useState(template.headerText || "");
  const [footerText, setFooterText] = useState(template.footerText || "");
  const [customMonths, setCustomMonths] = useState(
    [0,1,3,6,12,24].includes(template.expirationMonths || 0) ? "" : String(template.expirationMonths || "")
  );
  const isCustomExpiry = expirationMonths > 0 && ![1,3,6,12,24].includes(expirationMonths);

  const addField = (type = "text") => {
    const id = `f_${Date.now()}`;
    setFields(prev => [...prev, { id, type, label: type === "section-heading" ? "New Section" : type === "instruction" ? "Instruction text here..." : "New Field", required: false, options: [], placeholder: "", helpText: "", order: prev.length }]);
  };

  const updateField = (id, updated) => setFields(prev => prev.map(f => f.id === id ? updated : f));
  const deleteField = (id) => setFields(prev => prev.filter(f => f.id !== id));
  const moveUp = (idx) => { if (idx === 0) return; const f = [...fields]; [f[idx - 1], f[idx]] = [f[idx], f[idx - 1]]; setFields(f); };
  const moveDown = (idx) => { if (idx === fields.length - 1) return; const f = [...fields]; [f[idx], f[idx + 1]] = [f[idx + 1], f[idx]]; setFields(f); };

  const handleDrop = (dropTargetId) => {
    if (!draggingId || !dropTargetId || draggingId === dropTargetId) {
      setDraggingId(null); setDragOverId(null); return;
    }
    const from = fields.findIndex(f => f.id === draggingId);
    const to = fields.findIndex(f => f.id === dropTargetId);
    if (from === -1 || to === -1) return;
    const reordered = [...fields];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setFields(reordered);
    setDraggingId(null); setDragOverId(null);
  };

  const save = async (status) => {
    setSaving(true);
    try {
      const ordered = fields.map((f, i) => ({ ...f, order: i }));
      await onSave({ title, description, fields: ordered, status, expirationMonths, formDateFieldId: formDateFieldId || null, headerText, footerText, customStartDate: customStartDate || null });
      toast.success("Form saved");
    } catch {
      toast.error("Unable to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="form-builder-editor">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-slate-200 px-6 py-3 bg-white">
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        <input value={title} onChange={e => setTitle(e.target.value)}
          className="flex-1 text-lg font-bold text-slate-900 bg-transparent border-0 outline-none focus:ring-0" placeholder="Form title..." />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => save("draft")} disabled={saving} data-testid="save-draft-btn">
            <Save className="h-4 w-4 mr-1" /> Save Draft
          </Button>
          <Button size="sm" onClick={() => save("published")} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="publish-btn">
            <Eye className="h-4 w-4 mr-1" /> Publish
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Form Settings Panel — left column */}
        <div className="w-64 flex-shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50/50 px-3 py-4 space-y-4">

          {/* Quick Guide */}
          <details className="group">
            <summary className="cursor-pointer flex items-center justify-between text-[11px] font-bold text-teal-700 uppercase tracking-wider bg-teal-50 border border-teal-200 rounded-lg px-2 py-1.5 list-none select-none">
              <span>Quick Guide</span>
              <span className="text-teal-500 group-open:rotate-90 transition-transform inline-block">▶</span>
            </summary>
            <div className="mt-2 space-y-3 text-[11px] text-slate-600 leading-relaxed">

              <div className="rounded-lg bg-white border border-slate-200 p-2 space-y-1">
                <p className="font-bold text-slate-700">Settings Panel (this column)</p>
                <p><span className="font-semibold text-teal-700">Header/Footer:</span> Paste your state's required regulatory codes or citations here. They print at the top or bottom of every form automatically.</p>
                <p><span className="font-semibold text-teal-700">Expiration:</span> Set how often this form must be re-signed. When it expires, parents are notified to update and re-sign from their portal.</p>
                <p><span className="font-semibold text-teal-700">Date field:</span> Pick which date field the parent fills in — that date starts the expiration clock.</p>
              </div>

              <div className="rounded-lg bg-white border border-slate-200 p-2 space-y-1.5">
                <p className="font-bold text-slate-700">Field Types</p>
                <p><span className="font-semibold">Short Text</span> — one line answer (name, address)</p>
                <p><span className="font-semibold">Long Text</span> — multi-line (notes, descriptions)</p>
                <p><span className="font-semibold">Date</span> — calendar date picker</p>
                <p><span className="font-semibold">Phone / Email</span> — formatted input</p>
                <p><span className="font-semibold">Multiple Choice</span> — parent picks ONE option (radio buttons). Add your choices using the + Add option button in the field.</p>
                <p><span className="font-semibold">Checkboxes</span> — parent can pick MULTIPLE options. Each option is its own checkbox. Great for "Days needed" or "Meals required."</p>
                <p><span className="font-semibold">Single Checkbox</span> — one yes/no checkbox (e.g. "I agree to the above")</p>
                <p><span className="font-semibold">Dropdown</span> — same as Multiple Choice but shows as a menu</p>
                <p><span className="font-semibold">Signature</span> — parent draws their signature with finger or mouse. Legally binding under the ESIGN Act.</p>
                <p><span className="font-semibold">File Upload</span> — parent attaches a document or photo</p>
                <p><span className="font-semibold">Section Heading</span> — bold title that divides the form into sections (not a fill-in field)</p>
                <p><span className="font-semibold">Instruction Text</span> — italicized note or directions shown to the parent (not a fill-in field)</p>
              </div>

              <div className="rounded-lg bg-white border border-slate-200 p-2 space-y-1">
                <p className="font-bold text-slate-700">Tips</p>
                <p>• Click any field label to rename it</p>
                <p>• Use the ⠿ grip to drag fields into a new order</p>
                <p>• Toggle Required on fields parents must complete before submitting</p>
                <p>• Save Draft = hidden from parents · Publish = live in their portal</p>
                <p className="mt-1 pt-1 border-t border-slate-100"><span className="font-semibold text-teal-700">Auto-included on every form:</span> Your registered daycare organization name appears automatically at the top of all forms — you do not need to add it as a field.</p>
              </div>

            </div>
          </details>

          {/* Header / Footer text */}
          <div className="space-y-3">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">State Regulatory Text</p>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">
                Header - appears at top of form
              </label>
              <textarea value={headerText} onChange={e => setHeaderText(e.target.value)} rows={2}
                className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 resize-none bg-white"
                placeholder="e.g. In compliance with Reg. 3270.21. This form is required by [State] Department of Education..." />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">
                Footer - appears at bottom of form
              </label>
              <textarea value={footerText} onChange={e => setFooterText(e.target.value)} rows={2}
                className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 resize-none bg-white"
                placeholder="e.g. Ref: 55 Pa. Code § 3270.21(b). Retain on file for annual inspection." />
              <p className="text-[10px] text-slate-400 mt-1">Add your state's required codes, citations, or legal text. Appears on both digital and printed forms.</p>
            </div>
          </div>

          {/* Expiration Settings */}
          <div className="space-y-2">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Expiration Settings</p>
            <div className="flex items-start gap-2 flex-wrap">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Expires every</label>
                <select
                  value={isCustomExpiry ? "custom" : String(expirationMonths)}
                  onChange={e => {
                    if (e.target.value === "custom") {
                      setExpirationMonths(parseInt(customMonths) || 1);
                    } else {
                      setExpirationMonths(parseInt(e.target.value));
                      setCustomMonths("");
                    }
                  }}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
                  <option value="0">Never</option>
                  <option value="1">Monthly (1 month)</option>
                  <option value="3">3 months</option>
                  <option value="6">6 months</option>
                  <option value="12">12 months (yearly)</option>
                  <option value="24">24 months</option>
                  <option value="custom">Custom...</option>
                </select>
              </div>
              {(isCustomExpiry || (expirationMonths === 0 && customMonths)) && (
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Number of months</label>
                  <input type="number" min="1" max="120" value={customMonths}
                    onChange={e => { setCustomMonths(e.target.value); setExpirationMonths(parseInt(e.target.value) || 1); }}
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 w-24 bg-white"
                    placeholder="e.g. 18" />
                </div>
              )}
              {expirationMonths > 0 && (
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="text-xs text-slate-500">Date field that starts clock</label>
                    <div className="relative group">
                      <span className="text-[10px] text-slate-400 cursor-help border border-slate-300 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center font-bold">?</span>
                      <div className="absolute left-5 top-0 z-50 hidden group-hover:block w-52 bg-slate-800 text-white text-[10px] rounded-lg px-3 py-2 shadow-lg leading-relaxed">
                        <p className="font-semibold mb-1">What each option does:</p>
                        <p className="mb-1"><span className="text-amber-300">Use submission date</span> — clock starts the moment the parent hits Submit.</p>
                        <p className="mb-1"><span className="text-emerald-300">Custom date</span> — you pick a date (e.g. May 1) that auto-fills for every parent. Best for bulk sends.</p>
                        <p><span className="text-blue-300">A date field</span> — parent writes the date themselves. Clock starts from whatever they enter.</p>
                      </div>
                    </div>
                  </div>
                  <select value={formDateFieldId} onChange={e => { setFormDateFieldId(e.target.value); if (e.target.value !== "custom") setCustomStartDate(""); }}
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white max-w-[180px]">
                    <option value="">Use submission date</option>
                    <option value="custom">Custom date (calendar)</option>
                    {fields.filter(f => f.type === "date" && !f.label.toLowerCase().includes("birth") && !f.label.toLowerCase().includes("dob")).map(f => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </select>
                  {formDateFieldId === "custom" && (
                    <div className="mt-2">
                      <label className="text-xs text-slate-500 block mb-1">Set date to auto-fill</label>
                      <input type="date" value={customStartDate}
                        onChange={e => setCustomStartDate(e.target.value)}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white w-full" />
                      {customStartDate && (
                        <p className="text-[10px] text-emerald-600 mt-1">
                          Every parent who opens this form will have <strong>{new Date(customStartDate + "T12:00:00").toLocaleDateString("en-US", {month:"long", day:"numeric", year:"numeric"})}</strong> pre-filled. Change this date before each bulk send.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {expirationMonths > 0 && (
              <p className="text-[10px] text-slate-400">
                Parents notified 30 days before expiration and prompted to Update & Re-sign.
              </p>
            )}
          </div>

        </div>

        {/* Field list */}
        <div className="flex-1 overflow-y-auto p-6 space-y-2">
          <input value={description} onChange={e => setDescription(e.target.value)}
            className="w-full text-sm text-slate-500 bg-transparent border-0 border-b border-slate-200 outline-none pb-2 mb-4" placeholder="Form description (optional)..." />
          {fields.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No fields yet. Add fields from the right panel.</p>
            </div>
          )}
          {fields.map((field, idx) => (
            <FieldEditor key={field.id} field={field}
              onChange={updated => updateField(field.id, updated)}
              onDelete={() => deleteField(field.id)}
              onMoveUp={() => moveUp(idx)}
              onMoveDown={() => moveDown(idx)}
              isFirst={idx === 0} isLast={idx === fields.length - 1}
              onDragStart={id => setDraggingId(id)}
              onDragOver={id => setDragOverId(id)}
              onDrop={handleDrop}
              isDragOver={dragOverId === field.id && draggingId !== field.id}
            />
          ))}
        </div>

        {/* Add field panel */}
        <div className="w-52 border-l border-slate-200 bg-slate-50 p-4 space-y-1 overflow-y-auto flex-shrink-0">
          <p className="text-xs font-bold text-slate-500 uppercase mb-3">Add Field</p>
          {FIELD_TYPES.map(t => (
            <button key={t.value} onClick={() => addField(t.value)}
              className="w-full text-left text-xs text-slate-700 hover:bg-white hover:shadow-sm rounded-lg px-3 py-2 flex items-center gap-2 transition"
              data-testid={`add-field-${t.value}`}>
              <Plus className="h-3 w-3 text-emerald-500 flex-shrink-0" />
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function FormBuilderPage({ embedded = false }) {
  const [templates, setTemplates] = useState([]);
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("forms");
  const [submissions, setSubmissions] = useState([]);
  const [selectedSub, setSelectedSub] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [previewForm, setPreviewForm] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappings, setMappings] = useState({});
  const [autoMappingTitles, setAutoMappingTitles] = useState({});
  const [rowTypes, setRowTypes] = useState([]);
  const [savingMappings, setSavingMappings] = useState(false);
  const { organization } = useAuth();
  const logoUrl = `${BACKEND_URL}/simple-sign-in-logo.png`;
  const centerName = organization?.name || "Center Name";

  const fetchAll = async () => {
    try {
      const [tmpl, sys] = await Promise.all([
        axios.get(`${API}/forms/templates`),
        axios.get(`${API}/forms/system-templates`),
      ]);
      setTemplates(tmpl.data.templates || []);
      setSystemTemplates(sys.data.templates || []);
    } catch { toast.error("Unable to load forms"); }
    finally { setLoading(false); }
  };

  const fetchSubmissions = async () => {
    const res = await axios.get(`${API}/forms/submissions`);
    setSubmissions(res.data.submissions || []);
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (view === "submissions") fetchSubmissions(); }, [view]);

  // Load template mappings
  useEffect(() => {
    axios.get(`${API}/forms/template-mappings`).then(r => {
      setMappings(r.data.mappings || {});
      setRowTypes(r.data.formRowTypes || []);
      setAutoMappingTitles(r.data.autoMappingTitles || {});
    }).catch(() => {});
  }, []);

  const saveMappings = async () => {
    setSavingMappings(true);
    try {
      await axios.put(`${API}/forms/template-mappings`, { mappings });
      toast.success("Mappings saved");
      setShowMappingModal(false);
    } catch { toast.error("Unable to save mappings"); }
    finally { setSavingMappings(false); }
  };

  const [isCreating, setIsCreating] = useState(false);

  const createFromTemplate = async (sysKey) => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const sys = systemTemplates.find(t => t.key === sysKey);
      const res = await axios.post(`${API}/forms/templates`, {
        title: sys?.title || "New Form",
        systemTemplateKey: sysKey,
      });
      setShowTemplateModal(false);
      setEditing(res.data.template);
      fetchAll();
    } finally {
      setIsCreating(false);
    }
  };

  const createBlank = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const res = await axios.post(`${API}/forms/templates`, { title: "New Form" });
      setShowTemplateModal(false);
      setEditing(res.data.template);
      fetchAll();
    } finally {
      setIsCreating(false);
    }
  };

  const saveTemplate = async (updated) => {
    try {
      await axios.put(`${API}/forms/templates/${editing.id}`, updated);
      setEditing(null);
      await fetchAll();
    } catch {
      throw new Error("Save failed");
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/forms/templates/${id}`);
      fetchAll();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const duplicate = async (id) => {
    await axios.post(`${API}/forms/templates/${id}/duplicate`);
    fetchAll();
    toast.success("Duplicated");
  };

  const toggleStatus = async (t) => {
    const next = t.status === "published" ? "draft" : t.status === "archived" ? "draft" : "published";
    await axios.put(`${API}/forms/templates/${t.id}`, { status: next });
    fetchAll();
  };

  const archiveTemplate = async (id) => {
    await axios.put(`${API}/forms/templates/${id}`, { status: "archived" });
    fetchAll();
  };

  const markReviewed = async (id) => {
    await axios.put(`${API}/forms/submissions/${id}/status`, { status: "reviewed" });
    fetchSubmissions();
    toast.success("Marked as reviewed");
  };

  const handlePrintForm = (form) => buildPrintHtml(form, logoUrl, centerName);

  const handleDownloadPDF = (form) => {
    try {
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 40;
      const contentW = pageW - margin * 2;
      let y = margin;

      // Header: logo area + center name + form title
      doc.setDrawColor(15, 118, 110);
      doc.setLineWidth(1.5);

      // Center name
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(15, 118, 110);
      doc.text(centerName, pageW / 2, y + 12, { align: "center" });
      y += 24;

      // Form title
      doc.setFontSize(12);
      doc.setTextColor(51, 65, 85);
      doc.text(form.title, pageW / 2, y + 10, { align: "center" });
      y += 18;

      // Divider
      doc.line(margin, y, pageW - margin, y);
      y += 14;

      // Fields
      const fields = (form.fields || []).sort((a, b) => a.order - b.order);
      for (const f of fields) {
        // Page overflow check
        if (y > doc.internal.pageSize.getHeight() - 60) {
          doc.addPage();
          y = margin;
        }

        if (f.type === "section-heading") {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.setTextColor(15, 118, 110);
          doc.text(f.label, margin, y);
          y += 4;
          doc.setDrawColor(204, 251, 241);
          doc.line(margin, y, pageW - margin, y);
          y += 12;
          continue;
        }

        if (f.type === "instruction") {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(9);
          doc.setTextColor(100, 116, 139);
          const lines = doc.splitTextToSize(f.label, contentW - 16);
          doc.setFillColor(239, 246, 255);
          doc.roundedRect(margin, y - 10, contentW, lines.length * 11 + 10, 4, 4, "F");
          doc.text(lines, margin + 8, y);
          y += lines.length * 11 + 8;
          continue;
        }

        // Regular field
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(51, 65, 85);
        const label = f.label + (f.required ? " *" : "");
        doc.text(label, margin, y);
        if (f.required) {
          const lw = doc.getTextWidth(f.label + " ");
          doc.setTextColor(239, 68, 68);
          doc.text("*", margin + doc.getTextWidth(f.label) + 2, y);
          doc.setTextColor(51, 65, 85);
        }
        y += 4;

        if (f.helpText) {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8);
          doc.setTextColor(148, 163, 184);
          doc.text(f.helpText, margin, y);
          y += 10;
        }

        if (["text","email","phone","number","date","dropdown"].includes(f.type)) {
          doc.setDrawColor(203, 213, 225);
          doc.setFillColor(255, 255, 255);
          doc.roundedRect(margin, y, contentW, 20, 3, 3, "FD");
          y += 28;
        } else if (f.type === "textarea") {
          doc.setDrawColor(203, 213, 225);
          doc.setFillColor(255, 255, 255);
          doc.roundedRect(margin, y, contentW, 48, 3, 3, "FD");
          y += 56;
        } else if (f.type === "signature") {
          doc.setDrawColor(203, 213, 225);
          doc.setLineDashPattern([3, 2], 0);
          doc.rect(margin, y, contentW, 40);
          doc.setLineDashPattern([], 0);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(148, 163, 184);
          doc.text("Sign here", margin + contentW / 2, y + 22, { align: "center" });
          y += 50;
        } else if (["radio","checkbox-group","checkbox"].includes(f.type)) {
          const opts = f.type === "checkbox" ? ["Yes"] : (f.options || []);
          for (const opt of opts) {
            doc.setDrawColor(148, 163, 184);
            doc.setFillColor(255, 255, 255);
            if (f.type === "radio") doc.circle(margin + 5, y - 2, 4, "FD");
            else doc.rect(margin + 1, y - 6, 8, 8, "FD");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(71, 85, 105);
            doc.text(opt, margin + 14, y);
            y += 14;
          }
          y += 2;
        } else if (f.type === "file") {
          doc.setDrawColor(203, 213, 225);
          doc.roundedRect(margin, y, contentW, 20, 3, 3, "D");
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(148, 163, 184);
          doc.text("Attach file", margin + 8, y + 13);
          y += 28;
        }
      }

      const safeTitle = form.title.replace(/[^a-zA-Z0-9\s]/g, "").replace(/\s+/g, "_");
      doc.save(`${safeTitle}.pdf`);
    } catch (err) {
      toast.error("PDF download failed - use Print → Save as PDF instead");
    }
  };

  if (editing) {
    return <FormBuilderEditor template={editing} onSave={saveTemplate} onClose={() => setEditing(null)} />;
  }

  const PageWrapper = ({ children }) => embedded
    ? <div data-testid="forms-embedded">{children}</div>
    : <AppShell pageTitle="Enrollment Forms" breadcrumb>{children}</AppShell>;

  return (
    <PageWrapper>

      {/* ── Hero ── */}
      <div className="rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 p-6 mb-6 text-white" data-testid="forms-hero">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold mb-1">Enrollment Forms Builder</h1>
            <p className="text-teal-100 text-sm max-w-xl">Build, publish, and collect digital enrollment forms from parents. Start with one of your 6 built-in templates or create your own. Parents fill out and submit forms directly from their Family Portal.</p>
          </div>
          <button onClick={() => setShowInstructions(v => !v)}
            className="flex items-center gap-1.5 text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-full transition flex-shrink-0"
            data-testid="toggle-instructions-btn">
            <Info className="h-3.5 w-3.5" />
            {showInstructions ? "Hide Guide" : "How it works"}
          </button>
          <button onClick={() => setShowMappingModal(true)}
            className="flex items-center gap-1.5 text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-full transition flex-shrink-0"
            data-testid="open-mapping-btn">
            <Link2 className="h-3.5 w-3.5" />
            Child Record Mapping
          </button>
        </div>
      </div>

      {/* ── Instruction box ── */}
      {showInstructions && (
        <div className="rounded-2xl border border-teal-200 bg-teal-50 p-5 mb-6 space-y-3" data-testid="instructions-box">
          <p className="font-bold text-teal-800 flex items-center gap-2"><Layers className="h-4 w-4" />Director Guide - How Enrollment Forms Work</p>
          <div className="grid md:grid-cols-3 gap-3">
            {[
              { step: "1", title: "Create or Pick a Template", body: "Click New Form to start from scratch or choose one of your 6 pre-built templates (Emergency Contact, Enrollment, Financial, etc.). All templates are fully editable - change any label, add or remove fields." },
              { step: "2", title: "Publish to Parent Portal", body: "When your form is ready, click the eye icon to Publish it. Published forms instantly appear in the Enrollment Forms section of every parent's Family Portal. Draft forms are hidden from parents." },
              { step: "3", title: "Review Submissions", body: "When a parent submits a form, it appears in the Submissions inbox with a New badge. Open it to view all responses, then mark it as Reviewed. You'll also get an email notification." },
            ].map(s => (
              <div key={s.step} className="rounded-xl bg-white border border-teal-100 p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-6 h-6 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{s.step}</span>
                  <p className="text-sm font-semibold text-teal-800">{s.title}</p>
                </div>
                <p className="text-xs text-slate-600">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
            <p className="text-xs font-semibold text-amber-800 mb-1">Button Reference</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px] text-slate-600">
              <span className="flex items-center gap-1"><Edit3 className="h-3 w-3 text-slate-500" /> Edit form fields</span>
              <span className="flex items-center gap-1"><Glasses className="h-3 w-3 text-teal-500" /> Preview (parent view)</span>
              <span className="flex items-center gap-1"><ToggleRight className="h-3.5 w-3.5 text-emerald-500" /> Live - click to unpublish</span>
              <span className="flex items-center gap-1"><ToggleLeft className="h-3.5 w-3.5 text-slate-400" /> Draft - click to publish</span>
              <span className="flex items-center gap-1"><RotateCcw className="h-3 w-3 text-blue-500" /> Restore archived</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab bar ── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          <button onClick={() => setView("forms")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${view === "forms" ? "bg-white shadow text-slate-800" : "text-slate-500"}`} data-testid="forms-tab">
            <FileText className="h-4 w-4 inline mr-1" /> Forms
          </button>
          <button onClick={() => setView("submissions")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${view === "submissions" ? "bg-white shadow text-slate-800" : "text-slate-500"}`} data-testid="submissions-tab">
            <Inbox className="h-4 w-4 inline mr-1" /> Submissions
            {submissions.filter(s => s.status === "submitted").length > 0 && (
              <span className="ml-1 bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{submissions.filter(s => s.status === "submitted").length}</span>
            )}
          </button>
        </div>
        {view === "forms" && (
          <Button onClick={() => setShowTemplateModal(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-form-btn">
            <Plus className="h-4 w-4 mr-1" /> New Form
          </Button>
        )}
      </div>

      {/* Forms list */}
      {view === "forms" && (
        loading ? <div className="text-center py-16 text-slate-400">Loading...</div> :
        templates.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No forms yet</p>
            <p className="text-sm mt-1">Create a form from scratch or use one of your 6 built-in templates</p>
            <Button onClick={() => setShowTemplateModal(true)} className="mt-4 bg-emerald-600 text-white">Create First Form</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map(t => (
              <Card key={t.id} className="border border-slate-200" data-testid={`form-card-${t.id}`}>
                <CardContent className="flex items-center gap-4 p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900 truncate">{t.title}</p>
                      <Badge className={`text-[11px] ${STATUS_COLORS[t.status]}`}>{t.status}</Badge>
                      {t.systemTemplateKey && <Badge className="text-[11px] bg-blue-50 text-blue-600">Template</Badge>}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{t.fields?.length || 0} fields · Updated {new Date(t.updatedAt).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setEditing(t)} title="Edit form fields" data-testid={`edit-form-${t.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="sm" onClick={() => setPreviewForm(t)} title="Preview - see what parents see" data-testid={`preview-form-${t.id}`}>
                      <Glasses className="h-3.5 w-3.5 text-teal-500" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => toggleStatus(t)}
                      title={t.status === "published" ? "Published - now visible to Parents" : t.status === "archived" ? "Restore to Draft" : "Draft - click to publish"}
                      data-testid={`toggle-status-${t.id}`}>
                      {t.status === "published"
                        ? <ToggleRight className="h-4 w-4 text-emerald-500" />
                        : t.status === "archived"
                        ? <RotateCcw className="h-3.5 w-3.5 text-blue-500" />
                        : <ToggleLeft className="h-4 w-4 text-slate-400" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => duplicate(t.id)} title="Duplicate form" data-testid={`duplicate-form-${t.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="sm" onClick={() => archiveTemplate(t.id)} title="Archive form"><Archive className="h-3.5 w-3.5 text-slate-400" /></Button>
                    <Button variant="outline" size="sm" onClick={() => deleteTemplate(t.id)} title="Delete permanently" className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Submissions inbox */}
      {view === "submissions" && (
        <div className="space-y-3">
          {submissions.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Inbox className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No submissions yet</p>
              <p className="text-sm">Submissions appear here when parents complete and submit forms</p>
            </div>
          ) : submissions.map(s => (
            <Card key={s.id} className={`border cursor-pointer hover:shadow-md transition ${s.status === "submitted" ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200"}`}
              onClick={() => setSelectedSub(s)} data-testid={`submission-${s.id}`}>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">{s.formTitle}</p>
                  <p className="text-xs text-slate-500">{s.parentName} · {new Date(s.submittedAt).toLocaleDateString()}</p>
                </div>
                <Badge className={s.status === "reviewed" ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-700"}>
                  {s.status === "reviewed" ? "Reviewed" : "New"}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Template picker modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-picker-modal">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Create a New Form</CardTitle>
              <button onClick={() => setShowTemplateModal(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </CardHeader>
            <CardContent className="space-y-3">
              <button onClick={createBlank}
                className="w-full rounded-xl border-2 border-dashed border-slate-300 p-4 text-left hover:border-emerald-400 hover:bg-emerald-50 transition"
                data-testid="create-blank-form">
                <p className="font-semibold text-slate-800 flex items-center gap-2"><Plus className="h-4 w-4 text-emerald-600" />Start from Scratch</p>
                <p className="text-xs text-slate-500 mt-0.5">Build your own form with any fields you need</p>
              </button>
              <p className="text-xs font-bold text-slate-400 uppercase pt-2">Or start from a template</p>
              {systemTemplates.map(t => (
                <button key={t.key} onClick={() => createFromTemplate(t.key)}
                  className="w-full rounded-xl border border-slate-200 p-4 text-left hover:border-emerald-400 hover:bg-emerald-50 transition"
                  data-testid={`template-${t.key}`}>
                  <p className="font-semibold text-slate-800">{t.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{t.fields?.length || 0} fields - fully editable</p>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Submission detail modal */}
      {selectedSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submission-detail-modal">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <CardTitle>{selectedSub.formTitle}</CardTitle>
                <p className="text-sm text-slate-500">{selectedSub.parentName} · {new Date(selectedSub.submittedAt).toLocaleString()}</p>
              </div>
              <div className="flex gap-2">
                {selectedSub.status === "submitted" && (
                  <Button size="sm" onClick={() => { markReviewed(selectedSub.id); setSelectedSub({ ...selectedSub, status: "reviewed" }); }}
                    className="bg-emerald-600 text-white" data-testid="mark-reviewed-btn">
                    <Check className="h-4 w-4 mr-1" /> Mark Reviewed
                  </Button>
                )}
                <button onClick={() => setSelectedSub(null)}><X className="h-5 w-5 text-slate-400" /></button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(selectedSub.responses || {}).map(([fieldId, value]) => (
                <div key={fieldId} className="border-b border-slate-100 pb-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase">{fieldId}</p>
                  <p className="text-sm text-slate-800 mt-0.5">{Array.isArray(value) ? value.join(", ") : String(value || "—")}</p>
                </div>
              ))}
              {selectedSub.fileUploads?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Uploaded Files</p>
                  {selectedSub.fileUploads.map((f, i) => (
                    <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline block">{f.filename}</a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* ── Template Mapping Modal ── */}
      {showMappingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="mapping-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
              <div>
                <p className="font-bold text-slate-900 flex items-center gap-2"><Link2 className="h-4 w-4 text-teal-600" />Child Record Mapping</p>
                <p className="text-xs text-slate-500 mt-0.5">Auto-detected from your published forms. No manual setup needed — system templates are linked automatically.</p>
              </div>
              <button onClick={() => setShowMappingModal(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-700">
                Mapping is automatic. Forms you publish with a matching system template key are linked here, so digital submissions update the matching row in each child's record.
              </div>
              {rowTypes.map(([rowId, rowLabel]) => {
                const autoTitle = autoMappingTitles[rowId];
                return (
                  <div key={rowId} className="rounded-xl border border-slate-200 p-3 space-y-1.5" data-testid={`mapping-row-${rowId}`}>
                    <p className="text-xs font-semibold text-slate-700">{rowLabel}</p>
                    {autoTitle ? (
                      <div className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                        <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                        <span className="text-emerald-800">Auto-mapped to: <span className="font-semibold">{autoTitle}</span></span>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <span className="text-slate-600">⚪ No published template yet — publish a form with the matching starter to auto-map.</span>
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="flex gap-3 pt-2">
                <Button onClick={() => setShowMappingModal(false)}
                  className="flex-1 rounded-full bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="close-mappings-btn">
                  <Check className="h-4 w-4 mr-1" />Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Form Preview Modal ── */}
      {previewForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="form-preview-modal">
          <div className="w-full max-w-xl bg-[#f6fbfa] rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Preview toolbar */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 rounded-t-2xl flex items-center justify-between z-10">
              <p className="text-xs text-teal-600 font-semibold uppercase tracking-wide">Parent Preview</p>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => handleDownloadPDF(previewForm)}
                  variant="outline"
                  className="border-teal-300 text-teal-700 hover:bg-teal-50 rounded-full"
                  data-testid="download-pdf-btn">
                  <Download className="h-3.5 w-3.5 mr-1" /> Download PDF
                </Button>
                <Button size="sm" onClick={() => handlePrintForm(previewForm)}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                  data-testid="print-preview-btn">
                  <Printer className="h-3.5 w-3.5 mr-1" /> Print
                </Button>
                <button onClick={() => setPreviewForm(null)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Branded form header */}
              <div className="flex items-center border-b-2 border-teal-600 pb-4 gap-3">
                <img src={logoUrl} alt="Logo" className="h-11 w-11 object-contain flex-shrink-0"
                  onError={e => e.target.style.display = "none"} />
                <div className="flex-1 text-center">
                  <p className="text-lg font-bold text-teal-700">{centerName}</p>
                  <p className="text-sm font-semibold text-slate-700">{previewForm.title}</p>
                </div>
                <div className="w-11 flex-shrink-0" />
              </div>

              <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-2 text-xs text-amber-700 font-medium">
                Preview only - submissions are disabled
              </div>
              {/* Render fields in read-only demo mode */}
              {(previewForm.fields || []).sort((a, b) => a.order - b.order).map(field => {
                if (field.type === "section-heading") return (
                  <h3 key={field.id} className="text-base font-bold text-slate-800 border-b border-slate-200 pb-2 mt-2">{field.label}</h3>
                );
                if (field.type === "instruction") return (
                  <p key={field.id} className="text-sm text-slate-600 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 italic">{field.label}</p>
                );
                const shared = "w-full border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white opacity-70 pointer-events-none";
                return (
                  <div key={field.id} className="space-y-1">
                    <label className="text-sm font-medium text-slate-700">
                      {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    {field.helpText && <p className="text-xs text-slate-400">{field.helpText}</p>}
                    {["text","email","phone","number"].includes(field.type) && <input disabled placeholder={field.placeholder || field.label} className={shared} />}
                    {field.type === "date" && <input type="date" disabled className={shared} />}
                    {field.type === "textarea" && <textarea disabled placeholder={field.placeholder || field.label} rows={3} className={shared} />}
                    {field.type === "dropdown" && <select disabled className={shared}><option>{field.placeholder || "Select..."}</option></select>}
                    {["radio","checkbox-group"].includes(field.type) && (
                      <div className="space-y-1.5 opacity-70 pointer-events-none">
                        {(field.options || []).map(o => (
                          <div key={o} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 bg-white">
                            <input type={field.type === "radio" ? "radio" : "checkbox"} disabled />
                            <span className="text-sm text-slate-700">{o}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {field.type === "checkbox" && <div className="flex items-center gap-2 opacity-70 pointer-events-none"><input type="checkbox" disabled /><span className="text-sm text-slate-700">{field.placeholder || "Yes"}</span></div>}
                    {field.type === "signature" && <div className="h-24 border-2 border-dashed border-slate-300 rounded-xl bg-white opacity-70 flex items-center justify-center text-xs text-slate-400">Signature area</div>}
                    {field.type === "file" && <div className="border border-slate-300 rounded-xl px-3 py-2 text-sm text-slate-400 bg-white opacity-70">Choose file...</div>}
                  </div>
                );
              })}
              <div className="rounded-xl bg-emerald-600 opacity-40 text-white text-center py-3 text-sm font-semibold mt-2 pointer-events-none">Submit Form</div>
            </div>
          </div>
        </div>
      )}

    </PageWrapper>
  );
}
